import * as React from 'react'
import { FollowDialog } from '../../FollowDialog'
import { getClickableLink, getCorrectTimestamp, getStickerType } from '/fiweb/components/CampaignCard'
import { CampaignCardTemplateType, ClientCampaignCard } from '/fiweb/components/CampaignCard/types'
import { VisitorSettings } from '/~/types/graphql'
import { InvestmentOfferVisitorFragment } from '/~/types/issuer-graphql'
import { PreorderButton } from './PreorderButton'
import { getReducedClickableAreaProps, getStandardClickableAreaProps } from './clickableCardProps'
import dayjs from 'dayjs'
import { StickerTypeInvestmentOfferData } from '/fiweb/components/CampaignCard/utils'

interface CampaignData {
  reservationsSummary?: {
    total?: number
    shares?: number
    investors?: number
  }
  slug?: string
  raisingSharesMinimum?: number
  raisingSharesMaximum?: number
  raisingAmountMinimum?: number
  raisingAmountMaximum?: number
  status?: string
}

export interface NormalizeCardDataProps {
  cardData: ClientCampaignCard
  campaignData: CampaignData
  visitorSettings: VisitorSettings
  clickNavigatesTo?: string
  campaignToolMode?: boolean
  serverTimeOffset?: number
}

export const normalizeCardData = ({
  cardData,
  visitorSettings,
  campaignData,
  clickNavigatesTo,
  campaignToolMode,
  serverTimeOffset = 0,
}: NormalizeCardDataProps): CampaignCardTemplateType => {
  const isEmbed = window.self !== window.top
  const preorderActive = ['preallocating', 'published'].includes(campaignData?.status)
  const approvedForPreorder = preorderActive && !!visitorSettings?.approvedForPreorder
  const clickableLink =
    clickNavigatesTo || getClickableLink(cardData.state, cardData.slug, isEmbed, cardData.fromNewApi)

  let campaignCardTemplateData: CampaignCardTemplateType = {
    cardDataFromOldApi: true,
    imageUrl: cardData.imageUrl,
    title: cardData.title,
    caption: cardData.caption,
    cardType: cardData.state,
    followerCount: cardData.followerCount,
    slug: cardData.slug ?? '#',
    visitorIsFollowing: !!visitorSettings?.hasEmailReminder,
    campaignId: cardData.id,
    standardClickableAreaProps: getStandardClickableAreaProps(cardData.state, clickableLink),
    reducedClickableAreaProps: getReducedClickableAreaProps(cardData.state, clickableLink),
    followDialog: ({ open, onClose, campaignId }) => (
      <FollowDialog open={open} onClose={onClose} campaignId={campaignId} />
    ),
    timestamp: getCorrectTimestamp(cardData.state, {
      closeDate: cardData.dates?.close ? dayjs(cardData.dates?.close).valueOf() : undefined,
      startDate: cardData.dates?.open ? dayjs(cardData.dates?.open).valueOf() : undefined,
    }),
    websiteUrl: cardData.website,
    serverTimeOffset,
  }

  if (campaignToolMode) {
    delete campaignCardTemplateData.followDialog
    delete campaignCardTemplateData.websiteUrl
    delete campaignCardTemplateData.preorderButton
  }

  if (campaignData) {
    const stickerTypeProps: StickerTypeInvestmentOfferData = {
      state: cardData.state,
      maxShares: campaignData.raisingSharesMaximum,
      reservedShares: campaignData.reservationsSummary.shares,
      startDate: cardData.dates?.open,
    }
    campaignCardTemplateData = {
      ...campaignCardTemplateData,
      stickerType: getStickerType(
        stickerTypeProps,
        visitorSettings,
        !cardData?.dates?.open || dayjs(cardData.dates.open).isAfter(dayjs(Date.now() + serverTimeOffset)),
      ),
      reservedAmountInNok: campaignData.reservationsSummary.total,
      reservedPercent: (campaignData.reservationsSummary.shares / campaignData.raisingSharesMinimum) * 100,
      reservedPercentOfMax: (campaignData.reservationsSummary.shares / campaignData.raisingSharesMaximum) * 100,
      raisingAmountMinimum: campaignData.raisingAmountMinimum,
      raisingAmountMaximum: campaignData.raisingAmountMaximum,
      investorCount: campaignData.reservationsSummary.investors,
      preorderButton: (
        <PreorderButton visitorIsApprovedForPreorder={approvedForPreorder} url={`/kampanje/${campaignData.slug}`} />
      ),
    }
  }

  return campaignCardTemplateData
}

interface PublicInvestmentOffer {
  emissionProcessId: string
  maxSharesToIssue: number
  minSharesToIssue: number
  pricePerShare?: number
  totalAmountReserved: number
  totalSharesReserved: number
  subscribersCount: number
}

interface NormalizeNewCardDataProps {
  cardData: ClientCampaignCard
  publicInvestmentOffer?: PublicInvestmentOffer
  visitor?: InvestmentOfferVisitorFragment
  clickNavigatesTo?: string
  serverTimeOffset?: number
}

export const normalizeNewCardData = ({
  cardData,
  publicInvestmentOffer,
  visitor,
  clickNavigatesTo,
  serverTimeOffset = 0,
}: NormalizeNewCardDataProps): CampaignCardTemplateType => {
  if (!cardData) {
    return null
  }

  const isEmbed = window.self !== window.top
  const clickableLink =
    clickNavigatesTo || getClickableLink(cardData.state, cardData.slug, isEmbed, cardData.fromNewApi)

  let campaignCardTemplateData: CampaignCardTemplateType = {
    cardDataFromOldApi: false,
    imageUrl: cardData.imageUrl,
    title: cardData.title,
    caption: cardData.caption,
    cardType: cardData.state,
    followerCount: cardData.followerCount,
    slug: cardData.slug ?? '#',
    visitorIsFollowing: !!visitor?.isFollowing,
    emissionProcessId: cardData.id,
    standardClickableAreaProps: getStandardClickableAreaProps(cardData.state, clickableLink),
    reducedClickableAreaProps: getReducedClickableAreaProps(cardData.state, clickableLink),
    followDialog: ({ open, onClose, emissionProcessId }) => (
      <FollowDialog open={open} onClose={onClose} emissionProcessId={emissionProcessId} />
    ),
    timestamp: getCorrectTimestamp(cardData.state, {
      closeDate: cardData.dates?.close ? dayjs(cardData.dates?.close).valueOf() : undefined,
      startDate: cardData.dates?.open ? dayjs(cardData.dates?.open).valueOf() : undefined,
    }),
    websiteUrl: cardData.website,
    preorderButton: (
      <PreorderButton
        visitorIsApprovedForPreorder={visitor?.approvedForPreorder}
        url={`/investeringstilbud/${cardData.preorderSlug}`}
      />
    ),
  }

  if (publicInvestmentOffer) {
    const stickerTypeProps: StickerTypeInvestmentOfferData = {
      state: cardData.state,
      maxShares: publicInvestmentOffer.maxSharesToIssue,
      reservedShares: publicInvestmentOffer.totalSharesReserved,
      startDate: cardData.dates?.open,
    }
    campaignCardTemplateData = {
      ...campaignCardTemplateData,
      stickerType: getStickerType(
        stickerTypeProps,
        visitor,
        !cardData?.dates?.open || dayjs(cardData.dates.open).isAfter(dayjs(Date.now() + (serverTimeOffset ?? 0))),
      ),
      reservedAmountInNok: publicInvestmentOffer.totalAmountReserved / 100,
      reservedPercent: (publicInvestmentOffer.totalSharesReserved / publicInvestmentOffer.minSharesToIssue) * 100,
      reservedPercentOfMax: (publicInvestmentOffer.totalSharesReserved / publicInvestmentOffer.maxSharesToIssue) * 100,
      raisingAmountMinimum: publicInvestmentOffer.minSharesToIssue * publicInvestmentOffer.pricePerShare,
      raisingAmountMaximum: publicInvestmentOffer.maxSharesToIssue * publicInvestmentOffer.pricePerShare,
      investorCount: publicInvestmentOffer.subscribersCount,
    }
  }

  return campaignCardTemplateData
}
